import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { io as socketIO } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useOffline } from './OfflineContext';
import { productsAPI, salesAPI, attendantsAPI, securityRequestsAPI } from '../api';
import { cacheProducts, getCachedProducts } from '../utils/offlineDB';

// Socket URL – same logic as useSocket.js
const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return 'http://localhost:5000';
};

const DataCacheContext = createContext();

// Stale times (ms)
const STALE_TIMES = {
  products: 5 * 60 * 1000,        // 5 min – products rarely change mid-session
  sales: 30 * 1000,               // 30s – changes on every sale
  attendants: 10 * 60 * 1000,     // 10 min – very rarely change
  securityRequests: 2 * 60 * 1000  // 2 min
};

const createCacheEntry = () => ({
  data: null,
  fetchedAt: 0,
  isLoading: false,
  promise: null // dedup in-flight requests
});

export const DataCacheProvider = ({ children }) => {
  const { user } = useAuth();
  const { isOnline } = useOffline();
  const [, forceUpdate] = useState(0);
  const rerender = useCallback(() => forceUpdate(v => v + 1), []);

  // Cache refs (persist across re-renders without causing them)
  const cache = useRef({
    products: createCacheEntry(),
    sales: createCacheEntry(),
    attendants: createCacheEntry(),
    securityRequests: createCacheEntry()
  });

  // POS chunk prefetch status
  const posPrefetched = useRef(false);

  const isStale = useCallback((key) => {
    const entry = cache.current[key];
    if (!entry.data && !entry.isLoading) return true;
    return Date.now() - entry.fetchedAt > STALE_TIMES[key];
  }, []);

  // Generic fetch with dedup + stale-while-revalidate
  const fetchData = useCallback(async (key, fetcher, opts = {}) => {
    const entry = cache.current[key];
    const force = opts.force || false;

    // Return cached data if fresh
    if (!force && !isStale(key) && entry.data) {
      return entry.data;
    }

    // Dedup: if already fetching, return the existing promise
    if (entry.promise) {
      return entry.promise;
    }

    // Mark loading
    entry.isLoading = true;
    if (!entry.data) rerender(); // Only rerender if no stale data to show

    const promise = fetcher()
      .then(data => {
        entry.data = data;
        entry.fetchedAt = Date.now();
        entry.isLoading = false;
        entry.promise = null;
        rerender();
        return data;
      })
      .catch(err => {
        entry.isLoading = false;
        entry.promise = null;
        if (!entry.data) rerender(); // Only rerender if no fallback data
        throw err;
      });

    entry.promise = promise;
    return promise;
  }, [isStale, rerender]);

  // ---- Products ----
  const fetchProducts = useCallback(async (opts) => {
    return fetchData('products', async () => {
      if (isOnline) {
        try {
          const response = await productsAPI.getAll();
          const activeProducts = response.data.products.filter(p => p.isActive);
          // Cache for offline
          if (user?.tenantId) {
            cacheProducts(activeProducts, user.tenantId).catch(() => {});
          }
          return activeProducts;
        } catch (err) {
          // Fall through to offline cache
        }
      }
      // Offline fallback
      if (user?.tenantId) {
        const cached = await getCachedProducts(user.tenantId);
        return (cached || []).filter(p => p.isActive);
      }
      return [];
    }, opts);
  }, [fetchData, isOnline, user?.tenantId]);

  // ---- Today's Sales ----
  const fetchSales = useCallback(async (opts) => {
    return fetchData('sales', async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const response = await salesAPI.getAll({
        startDate: today.toISOString(),
        ownSalesOnly: 'true'
      });
      return response.data.sales || [];
    }, opts);
  }, [fetchData]);

  // ---- Attendants ----
  const fetchAttendants = useCallback(async (opts) => {
    return fetchData('attendants', async () => {
      const response = await attendantsAPI.getAll({ isActive: 'true' });
      return response.data.attendants || [];
    }, opts);
  }, [fetchData]);

  // ---- Security Requests ----
  const fetchSecurityRequests = useCallback(async (opts) => {
    return fetchData('securityRequests', async () => {
      const response = await securityRequestsAPI.getAll();
      const requests = response.data.securityRequests || [];
      const requestMap = {};
      requests.forEach(req => {
        if (req.saleId) {
          // If the void was approved but the sale has been reinstated (completed), skip it
          const isReinstated = req.status === 'APPROVED' && req.type === 'VOID' && req.sale?.paymentStatus === 'completed';
          if (isReinstated) return;
          requestMap[req.saleId] = {
            id: req.id,
            type: req.type.toLowerCase(),
            status: req.status.toLowerCase(),
            reason: req.reason
          };
        }
      });
      return requestMap;
    }, opts);
  }, [fetchData]);

  // ---- Invalidation ----
  const invalidate = useCallback((key) => {
    cache.current[key].fetchedAt = 0; // Mark stale
  }, []);

  const invalidateProducts = useCallback(() => invalidate('products'), [invalidate]);
  const invalidateSales = useCallback(() => invalidate('sales'), [invalidate]);
  const invalidateAll = useCallback(() => {
    Object.keys(cache.current).forEach(k => invalidate(k));
  }, [invalidate]);

  // ---- POS Chunk Prefetch ----
  const prefetchPOSChunks = useCallback((businessType) => {
    if (posPrefetched.current) return;
    posPrefetched.current = true;

    const retailTypes = ['RETAIL', 'GROCERY', 'ELECTRONICS', 'PHARMACY', 'CLOTHING'];
    const serviceTypes = ['SERVICES', 'SALON'];
    const foodTypes = ['FOOD_AND_BEVERAGE', 'RESTAURANT'];

    // Prefetch the appropriate POS chunk based on business type
    if (retailTypes.includes(businessType)) {
      import('../pages/RetailPOS').catch(() => {});
    } else if (foodTypes.includes(businessType)) {
      import('../pages/RestaurantPOS').catch(() => {});
    } else if (serviceTypes.includes(businessType)) {
      import('../pages/ServicePOS').catch(() => {});
    }
  }, []);

  // ---- Background Prefetch on Auth ----
  // When user is authenticated, start warming the cache
  useEffect(() => {
    if (!user?.tenantId || !isOnline) return;

    // Warm product cache in background (most important for POS speed)
    fetchProducts().catch(() => {});

    // Prefetch POS code chunk
    if (user.businessType) {
      prefetchPOSChunks(user.businessType);
    }
  }, [user?.tenantId, isOnline, user?.businessType, fetchProducts, prefetchPOSChunks]);

  // ---- Re-fetch products when coming back online ----
  const wasOnline = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !wasOnline.current) {
      // Just came back online – refresh stale data
      fetchProducts({ force: true }).catch(() => {});
      fetchSales({ force: true }).catch(() => {});
      fetchAttendants({ force: true }).catch(() => {});
      fetchSecurityRequests({ force: true }).catch(() => {});
    }
    wasOnline.current = isOnline;
  }, [isOnline, fetchProducts, fetchSales, fetchAttendants, fetchSecurityRequests]);

  // ---- Refresh caches after offline sync ----
  useEffect(() => {
    const handleOfflineSync = () => {
      fetchProducts({ force: true }).catch(() => {});
      fetchSales({ force: true }).catch(() => {});
    };
    window.addEventListener('offlineSyncComplete', handleOfflineSync);
    return () => window.removeEventListener('offlineSyncComplete', handleOfflineSync);
  }, [fetchProducts, fetchSales]);

  // ─── Real-time sync via Socket.IO ────────────────────────
  const socketRef = useRef(null);
  // Track page-level refetch callbacks (pages register via onDataChanged)
  const listenersRef = useRef({});

  useEffect(() => {
    if (!user?.tenantId) return;

    const socket = socketIO(getSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000
    });
    socketRef.current = socket;

    const handleConnect = () => {
      socket.emit('join-tenant', user.tenantId);
    };

    const handleDataChanged = ({ dataType }) => {
      // Invalidate cache for known types
      if (dataType === 'products') {
        invalidate('products');
        fetchProducts({ force: true }).catch(() => {});
      } else if (dataType === 'sales') {
        invalidate('sales');
        fetchSales({ force: true }).catch(() => {});
      } else if (dataType === 'attendants') {
        invalidate('attendants');
        fetchAttendants({ force: true }).catch(() => {});
      } else if (dataType === 'securityRequests') {
        invalidate('securityRequests');
        fetchSecurityRequests({ force: true }).catch(() => {});
      }

      // Notify page-level listeners (for data not in the global cache)
      Object.values(listenersRef.current).forEach(cb => {
        try { cb(dataType); } catch {}
      });
    };

    socket.on('connect', handleConnect);
    socket.on('data:changed', handleDataChanged);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('data:changed', handleDataChanged);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.tenantId, invalidate, fetchProducts, fetchSales, fetchAttendants, fetchSecurityRequests]);

  // Pages call this to subscribe to data:changed for types they care about
  const onDataChanged = useCallback((id, callback) => {
    listenersRef.current[id] = callback;
    return () => { delete listenersRef.current[id]; };
  }, []);

  const value = {
    // Data accessors (return cached data + loading state)
    getProducts: () => cache.current.products,
    getSales: () => cache.current.sales,
    getAttendants: () => cache.current.attendants,
    getSecurityRequests: () => cache.current.securityRequests,

    // Fetch functions (stale-while-revalidate)
    fetchProducts,
    fetchSales,
    fetchAttendants,
    fetchSecurityRequests,

    // Invalidation
    invalidateProducts,
    invalidateSales,
    invalidateAll,

    // Prefetch
    prefetchPOSChunks,

    // Real-time sync – pages register for non-cached data types
    onDataChanged
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => {
  const ctx = useContext(DataCacheContext);
  if (!ctx) throw new Error('useDataCache must be used within DataCacheProvider');
  return ctx;
};
